import React, { useState, useEffect, useCallback } from 'react';

import { downloadFile, extractFileNameFromHeaders } from '../../../../util/DownloadUtil';

import {
  retrieveLiveSchedulerStudyReport,
  downloadLiveSchedulerStudyReport,
  retrieveLiveSchedulerParticipantReport,
  downloadLiveSchedulerParticipantReport,
  retrieveLiveSchedulerParticipantVisitReport,
  downloadLiveSchedulerParticipantVisitReport,
} from '../../../../services/ReportService';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Table } from '../../../common/table/Table';
import { BackButton } from '../../../common/button/back-button/BackButton';

import './LiveScheduler.css';

export const LiveScheduler = ({ context, setSpinnerText, showToaster, downloadEnabled }) => {
  const [studyResults, setStudyResults] = useState([]);
  const [participantResults, setPartipantResults] = useState([]);
  const [visitResults, setVisitResults] = useState([]);
  const [study, setStudy] = useState(undefined);
  const [participant, setParticipant] = useState(undefined);

  const generateStudyReport = useCallback(async () => {
    setSpinnerText('Generating Study Report');
    const [data, error] = await retrieveLiveSchedulerStudyReport(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        setStudyResults(
          data.liveSchedulerPerStudyVOS.map((vo) => ({
            studyName: vo.studyName,
            site: vo.site,
            uncapturedVisits: vo.uncpaturedVisits,
            missedVisits: vo.missedVisits,
            outOfWindowVisits: vo.outOfWindowVisits,
            unscheduledVisits: vo.unscheduledVisits,
            successfulVisits: vo.successfulVisits,
            locationExternalReference: vo.locationExternalReference,
            studyExternalReference: vo.studyExternalReference,
          }))
        );
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  }, [context, setSpinnerText, showToaster]);

  useEffect(() => {
    generateStudyReport();
  }, [generateStudyReport]);

  const handleStudyClick = async (study) => {
    setSpinnerText('Generating Participant Report');
    const [data, error] = await retrieveLiveSchedulerParticipantReport(
      study.locationExternalReference,
      study.studyExternalReference,
      context.username
    );
    if (data) {
      if (data.responseStatus === 'Success') {
        setStudy(study);
        setPartipantResults(
          data.liveSchedulerPerParticipantVOS.map((vo) => ({
            participantID: vo.participantID,
            uncapturedVisits: vo.uncpaturedVisits,
            missedVisits: vo.missedVisits,
            outOfWindowVisits: vo.outOfWindowVisits,
            unscheduledVisits: vo.unscheduledVisits,
            successfulVisits: vo.successfulVisits,
            participantExternalReference: vo.participantExternalReference,
          }))
        );
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  };

  const formatDate = (date) => {
    if (!date) return '-';

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const handleParticipantClick = async (participant) => {
    setSpinnerText('Generating Visit Report');
    const [data, error] = await retrieveLiveSchedulerParticipantVisitReport(
      participant.participantExternalReference,
      context.username
    );
    if (data) {
      if (data.responseStatus === 'Success') {
        setParticipant(participant);
        setVisitResults(
          data.liveSchedulerPerParticipantVisitVOS.map((vo) => ({
            vistCode: vo.visitCode,
            visitType: vo.visitType,
            dateOfVisit: formatDate(new Date(vo.dateOfVisit)),
            daysFromTargetDate: vo.daysOutsideWindowPeriod,
            successfulVisits: vo.successfulVisits,
          }))
        );
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  };

  const handleBackClick = () => {
    if (participant) {
      setParticipant(undefined);
      return;
    } else if (study) {
      setStudy(undefined);
    }
  };

  const handleDownloadExcelClick = async () => {
    let data;
    let headers;
    let error;

    if (!study && !participant) {
      setSpinnerText('Downloading Live Scheduler per Study Report');
      [data, headers, error] = await downloadLiveSchedulerStudyReport(context.username);
    } else if (study && !participant) {
      setSpinnerText('Downloading Live Scheduler per Participant Report');
      [data, headers, error] = await downloadLiveSchedulerParticipantReport(
        study.locationExternalReference,
        study.studyExternalReference,
        context.username
      );
    } else {
      setSpinnerText('Downloading Live Scheduler per Participant Visit Report');
      [data, headers, error] = await downloadLiveSchedulerParticipantVisitReport(
        participant.participantExternalReference,
        context.username
      );
    }

    if (data) {
      const name = extractFileNameFromHeaders(headers);
      downloadFile(data, name);
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(undefined);
  };

  const renderTable = () => {
    let tableData = studyResults;
    let tableTitle = ' ';
    let handleRowClick = handleStudyClick;
    let hiddenColumns = ['locationExternalReference', 'studyExternalReference'];

    if (study && !participant) {
      tableData = participantResults;
      tableTitle = study.studyName;
      handleRowClick = handleParticipantClick;
      hiddenColumns = ['participantExternalReference'];
    } else if (study && participant) {
      tableData = visitResults;
      tableTitle = `Participant: ${participant.participantID} - ${study.studyName}`;
      handleRowClick = undefined;
      hiddenColumns = [];
    }

    return (
      <Table
        tableData={tableData}
        tableTitle={tableTitle}
        onRowClick={handleRowClick}
        hiddenColumns={hiddenColumns}
        maxRowCount={8}
        searchEnabled
        filterEnabled
        horizontalScroll
      />
    );
  };

  let heading = 'Live Scheduler per Participant Visits';
  let subheading = '\u00A0';
  if (!study && !participant) {
    heading = 'Live Scheduler per Study';
    subheading = '*Click on a record to view participants';
  } else if (!participant) {
    heading = 'Live Scheduler per Participant';
    subheading = '*Click on a record to view participant visit details';
  }

  return (
    <section className="live-scheduler">
      <div className="live-scheduler-table">
        <div className="live-scheduler-table-heading-container">
          {(study || participant) && (
            <div className="live-scheduler-back-button">
              <BackButton onClick={handleBackClick} />
              <p>Back</p>
            </div>
          )}
          <h1 className="live-scheduler-table-heading">{heading}</h1>
          <h2 className="live-scheduler-table-subheading">{subheading}</h2>
        </div>
        {renderTable()}
      </div>
      {downloadEnabled && (
        <div className="live-scheduler-table-buttons">
          <SaveButton
            title="Download Excel"
            onClick={handleDownloadExcelClick}
            color="#077187"
            border="1px solid #077187"
          />
        </div>
      )}
    </section>
  );
};
